import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

// ============================================================
// Modal de confirmación propio (reemplaza window.confirm del navegador)
// Uso: if (!(await confirmDialog({ description: '...' }))) return;
// ============================================================

export type ConfirmOpts = {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
};

let _openConfirm: ((o: ConfirmOpts) => Promise<boolean>) | null = null;

// API imperativa: `if (!(await confirmDialog({...}))) return;`
export function confirmDialog(o: ConfirmOpts | string): Promise<boolean> {
  const opts: ConfirmOpts = typeof o === 'string' ? { description: o } : o;
  if (_openConfirm) return _openConfirm(opts);
  // Fallback por si el host no está montado
  return Promise.resolve(window.confirm(opts.description || opts.title || ''));
}

export const ConfirmDialogHost: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOpts>({});
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  useEffect(() => {
    _openConfirm = (o) =>
      new Promise<boolean>((resolve) => {
        setOpts(o);
        resolverRef.current = resolve;
        setOpen(true);
      });
    return () => {
      _openConfirm = null;
    };
  }, []);

  const finish = (value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(false); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{opts.title || 'Confirmar'}</DialogTitle>
          {opts.description ? <DialogDescription>{opts.description}</DialogDescription> : null}
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => finish(false)}>
            {opts.cancelText || 'Cancelar'}
          </Button>
          <Button
            className={opts.destructive
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            onClick={() => finish(true)}
          >
            {opts.confirmText || 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialogHost;
